import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { take } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import ODataStore from 'devextreme/data/odata/store';
import { BdsDialogInfoComponent, BdsDialogInfoModel } from '@bds/components';
import { RtBusinessGroup, RtFleetName, RtWhoPays } from '@bds/railtrac-models';
import { BdsBusinessGroupService } from '../../bds-business-group/bds-business-group.service';
import { BdsFleetService } from '../../bds-fleet/bds-fleet.service';
import { BdsErpcOriginService } from '@bds/data-access';
import { RtRouteService } from '@bds/data-access';
import { RtDiversionService } from '../services/rt-diversion.service';
import { RtTripsByRoad } from '../models/rt-trips-by-road.model';
import { RtDiversionFormModel } from '../models/rt-diversion-form.model';
import * as moment from 'moment';
import { AuthorizeService } from '@bds/auth';
import { faCodeBranch, faFloppyDisk, faPlus } from '@fortawesome/pro-solid-svg-icons';
import { RtDiversionDetailsComponent } from '../rt-diversion-details/rt-diversion-details.component';
import {
    RtTripShopFormDialogComponent,
    RtTripShopFormDialogOptions,
} from '../../rt-shop-form/rt-shop-form-dialog/rt-shop-form-dialog.component';
import { RailtracTripService } from '@bds/railtrac';

@Component({
    selector: 'rt-new-diversion',
    templateUrl: './rt-new-diversion.component.html',
    styleUrls: ['./rt-new-diversion.component.scss'],
})
export class RtNewDiversionComponent implements OnChanges, OnDestroy {
    businessGroupList$: Observable<RtBusinessGroup[]>;
    diversion: RtDiversionFormModel;
    divertTypes$: Observable<{ key: string; value: string }[]>;
    errorMessage = '';
    fleetList$: Observable<RtFleetName[]>;
    hasLatestTripsToDivert = false;
    isSaving = false;
    isFormValid = false;
    routeList: ODataStore;
    showCreateDiversion$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    splcList: ODataStore;
    whoPays$: Observable<RtWhoPays[]>;
    divertDateTime: Date | string;
    faCodeBranch = faCodeBranch;
    faFloppyDisk = faFloppyDisk;
    faPlus = faPlus;

    @Input() creatingDiversion: boolean;
    @Input() tripIds: number[] = [];
    @Output() diversionCancelled: EventEmitter<any> = new EventEmitter();
    @Output() diversionCreated: EventEmitter<any> = new EventEmitter();
    @ViewChild('addDiversionComponent') addDiversionComponent!: RtDiversionDetailsComponent;

    constructor(
        public diversionService: RtDiversionService,
        public splcService: BdsErpcOriginService,
        public routeService: RtRouteService,
        public fleetService: BdsFleetService,
        public businessGroupService: BdsBusinessGroupService,
        public railtracTripService: RailtracTripService,
        public dialog: MatDialog,
        public snackbar: MatSnackBar,
        public auth: AuthorizeService,
    ) {
        this.divertTypes$ = diversionService.getDivertTypes();
        this.businessGroupList$ = businessGroupService.getBusinessGroups();
        this.fleetList$ = fleetService.getFleetNames();
        this.whoPays$ = diversionService.getWhoPaysList();
        this.splcList = splcService.getODataStore();
        this.routeList = routeService.getODataStore();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.creatingDiversion && this.creatingDiversion !== undefined) {
            if (this.tripIds && this.creatingDiversion && !this.hasLatestTripsToDivert) {
                this.getTripsForDiversion();
                this.showCreateDiversion$.next(this.creatingDiversion);
            } else if (!this.creatingDiversion) {
                this.showCreateDiversion$.next(this.creatingDiversion);
            }
        }

        if (changes.tripIds && !changes.tripIds.firstChange) {
            this.hasLatestTripsToDivert = false;
            this.cancelDiversion();
        }
    }

    ngOnDestroy() {
        this.showCreateDiversion$.complete();
    }

    cancelDiversion(): void {
        this.showCreateDiversion$.next(false);
        this.errorMessage = '';
        this.diversionCancelled.emit();
    }

    createDiversion(roads: RtTripsByRoad[]): void {
        if (this.roadsHaveTrips(roads)) {
            this.diversion = {
                header: {
                    divertDateTime: null,
                    divertType: null,
                    le: roads[0].diversions[0].clmLe,
                    newCustCity: null,
                    newCustName: null,
                    newCustNo: null,
                    newCustState: null,
                    newReturnCity: null,
                    newReturnState: null,
                    newReturnSplc: null,
                    shipmentType: null,
                    whoPays: null,
                },

                roads: roads,
            };
        }
    }

    getShowCreateDiversion(): Observable<boolean> {
        return this.showCreateDiversion$.asObservable();
    }

    getTripsForDiversion(): void {
        this.diversionService
            .getTripsToDivert(this.tripIds)
            .pipe(take(1))
            .subscribe(
                (roads) => {
                    if (roads.find((r) => r['isCurrent'] === false)) {
                        const info: BdsDialogInfoModel = {
                            title: 'Divert Error',
                            content:
                                'Diversions are only allowed on current trips. Please close this trip and divert the current trip.',
                        };
                        this.dialog.open(BdsDialogInfoComponent, {
                            data: info,
                        });
                        return;
                    }
                    this.showCreateDiversion$.next(true);
                    this.hasLatestTripsToDivert = true;
                    return this.createDiversion(roads);
                },
                (err) => {
                    if (err && err['status'] === 422) {
                        const info: BdsDialogInfoModel = {
                            title: 'Divert Error',
                            content: err['message'] || 'Unable to divert selected trip(s).',
                        };
                        this.dialog.open(BdsDialogInfoComponent, {
                            data: info,
                        });
                    } else {
                        this.snackbar.open('Error retrieving trips!', 'Ok', {
                            duration: 3000,
                            panelClass: 'error-snack',
                        });
                    }
                    this.diversionCancelled.emit();
                },
            );
    }

    onCancelClick(): void {
        this.cancelDiversion();
        this.diversion = { ...this.diversion };
    }

    onCreateClick(): void {
        if (!this.hasLatestTripsToDivert) {
            this.getTripsForDiversion();
        } else {
            this.showCreateDiversion$.next(true);
        }
    }

    async onSaveClick(event: RtDiversionFormModel): Promise<void> {
        this.isSaving = true;
        this.errorMessage = '';
        const user = await firstValueFrom(this.auth.getUser());

        if (user) {
            event.header.userId = user?.preferred_username;
        }
        this.divertDateTime = event.header.divertDateTime;
        event.header.divertDateTime =
            moment(event.header.divertDateTime).format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z';
        const request = { ...event };

        this.diversionService
            .create(request)
            .pipe(take(1))
            .subscribe(
                (response) => {
                    this.afterSaveComplete();
                    this.openShopFormDialog(event);
                    this.hasLatestTripsToDivert = false;
                },
                (err) => {
                    event.header.divertDateTime = this.divertDateTime;
                    this.afterError(err);
                },
            );
    }

    openShopFormDialog(diversion: RtDiversionFormModel): void {
        if (diversion?.header.divertType !== 'S') {
            return;
        }

        if (!diversion) {
            this.snackbar.open('No trip data available for shop form', 'Close', { duration: 5000 });
            return;
        }

        // Validate required fields before opening dialog
        if (!diversion.roads[0].diversions[0].carInit || !diversion.roads[0].diversions[0].carNo) {
            this.snackbar.open('Car Init and Car No are required to create shop form', 'Close', {
                duration: 5000,
            });
            return;
        }

        const carInit = diversion?.roads[0]?.diversions[0]?.carInit;
        const carNo = diversion?.roads[0]?.diversions[0]?.carNo;
        const shipDateTime =
            typeof diversion?.header?.divertDateTime === 'string'
                ? new Date(diversion.header.divertDateTime)
                : diversion?.header?.divertDateTime;
        const custNo = diversion?.header?.newCustNo;
        let tripOrmId: number;

        this.railtracTripService
            .getTrip(carInit, carNo, shipDateTime)
            .pipe(take(1))
            .subscribe((trip) => {
                if (!trip) {
                    this.snackbar.open('Trip data not found for shop form', 'Close', {
                        duration: 5000,
                    });
                    return;
                }
                tripOrmId = trip?.ormId;
            });

        const dialogRef = this.dialog.open(RtTripShopFormDialogComponent, {
            panelClass: 'shop-form-dialog',
            disableClose: true,
            data: {
                title: `Shop Form for Car ${carInit}${carNo}`,
                mode: 'create',
                carInit: carInit,
                carNo: carNo,
                tripOrmId: tripOrmId,
                shipDateTime: shipDateTime,
                custNo: custNo,
                readonly: false,
                generate: true,
            } as RtTripShopFormDialogOptions,
        });

        dialogRef.afterClosed().subscribe((result) => {
            if (result) {
                this.snackbar.open(
                    `Shop form updated successfully for ${carInit}${carNo}`,
                    'Close',
                    { duration: 3000 },
                );
            }
        });
    }

    roadsHaveTrips(roads: RtTripsByRoad[]): boolean {
        if (
            roads !== undefined &&
            roads.length > 0 &&
            roads[0].diversions !== undefined &&
            roads[0].diversions.length > 0
        ) {
            return true;
        }

        return false;
    }

    private afterError(error: any) {
        this.errorMessage = error ? error : 'Error saving diversions!';

        if (error && error['status'] === 422 && error['error']) {
            this.errorMessage = error['error'];
        }

        this.snackbar.open(this.errorMessage, 'Error', {
            duration: 3000,
            panelClass: 'error-snack',
        });
        this.isSaving = false;
    }

    private afterSaveComplete(message?: string) {
        if (!message) {
            message = 'Changes saved!';
        }

        this.isSaving = false;
        this.snackbar.open(message, 'Ok', { duration: 3000 });
        this.showCreateDiversion$.next(false);
        this.diversion = { ...this.diversion };
        this.diversionCreated.emit();
    }

    saveDiversion(): void {
        this.addDiversionComponent.onSubmit();
    }

    onFormValidityChange(validity: boolean): void {
        this.isFormValid = validity;
    }
}
